import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, FloatButton, Pagination } from "antd";
import type { PaginationProps } from "antd";
import { UploadOutlined, FileTextOutlined } from "@ant-design/icons";
import DataProvider from "../../services";
import Loader from "../../components/cards/loader";
import Empty from "../../components/elements/empty";
import PrescriptionOrderCard from "../../components/cards/prescription-order-card";
import ChatButton from "../../components/cards/message-card/chatButton";
import ChatInterface from "../../components/sections/chatai";
import whatsapp from "../../assets/images/WhatsApp.png";
import kobi from "../../assets/images/KUUL TRANS ROUND.png";
import type { PrescriptionOrder, PrescriptionOrderDetail } from "../../types/prescription";
import { getStatusConfig } from "../../types/prescription";
import moment from "moment";

function formatGHC(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `GHC ${Number(value).toLocaleString("en-GH", { minimumFractionDigits: 2 })}`;
}

// Insurance policy numbers are shown masked by default (only the last 4
// characters visible) rather than rendered in full - this is the account
// owner's own data, so the risk is limited to shoulder-surfing/screen-share
// scenarios, but there's no reason to show the whole thing on screen.
function maskPolicyNumber(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length <= 4) return trimmed;
  return `${"•".repeat(trimmed.length - 4)}${trimmed.slice(-4)}`;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === "") return null;
  return (
    <div className="py-2 border-b border-neutral-100 last:border-0">
      <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-0.5">
        {label}
      </span>
      <p className="text-sm text-neutral-900 break-words">{value}</p>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-2">
        {title}
      </h3>
      <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-3">
        {children}
      </div>
    </section>
  );
}

declare global {
  interface Window {
    ebanqo_widget: any;
  }
}

const PAGE_SIZE = 9;

export default function Prescription() {
  const navigate = useNavigate();
  const [data, setData] = useState<PrescriptionOrder[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState<PrescriptionOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<PrescriptionOrderDetail | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await DataProvider.getData(currentPage, PAGE_SIZE);
      setData(results.data?.prescriptions ?? null);
      setTotalPages(results.data?.totalPages ?? 0);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processingOrders =
    data != null ? data.filter((o) => String(o.status).toLowerCase() === "processing") : [];
  const hasProcessing = processingOrders.length > 0;

  const onChangePage: PaginationProps["onChange"] = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (order: PrescriptionOrder) => {
    setDetailOrder(order);
    setDetailData(null);
    setDetailLoading(true);
    DataProvider.getOrder(String(order.id))
      .then((res: { data?: PrescriptionOrderDetail }) => {
        setDetailData(res?.data ?? null);
      })
      .catch(() => setDetailData(null))
      .finally(() => setDetailLoading(false));
  };

  const closeDetailDrawer = () => {
    setDetailOrder(null);
    setDetailData(null);
  };

  const isEmpty = data !== null && data.length === 0;
  const showList = data != null && data.length > 0;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900 tracking-tight">
            Prescription orders
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            View, track, and manage your uploaded prescriptions for medication fulfillment.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Upload CTA */}
        <section
          className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 sm:p-5"
          aria-labelledby="upload-prescription-heading"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2
                id="upload-prescription-heading"
                className="text-base font-semibold text-neutral-900"
              >
                New prescription
              </h2>
              <p className="mt-1 text-sm text-neutral-600">
                Upload a prescription image to request medication fulfillment.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/main/prescriptions/ordering")}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#06509E] text-white text-sm font-medium hover:bg-[#053d7a] focus:outline-none focus:ring-2 focus:ring-[#06509E] focus:ring-offset-2 transition-colors flex-shrink-0"
              aria-label="Upload prescription image"
            >
              <UploadOutlined aria-hidden />
              Upload prescription
            </button>
          </div>
        </section>

        {/* In progress (processing) orders */}
        {hasProcessing && (
          <section aria-labelledby="in-progress-heading">
            <h2
              id="in-progress-heading"
              className="text-sm font-semibold text-neutral-700 mb-3"
            >
              In progress
            </h2>
            <ul className="space-y-3" aria-label="Prescription orders in progress">
              {processingOrders.map((order) => (
                <li key={order.id}>
                  <PrescriptionOrderCard order={order} onViewDetails={handleViewDetails} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* All orders */}
        <section aria-labelledby="all-orders-heading">
          <h2
            id="all-orders-heading"
            className="text-sm font-semibold text-neutral-700 mb-3"
          >
            All orders
          </h2>

          {loading && (data === null || data.length === 0) ? (
            <div className="bg-white rounded-xl border border-neutral-200 p-6">
              <Loader num={3} />
            </div>
          ) : isEmpty ? (
            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
              <Empty
                imageStyle="md:h-44 h-24"
                titleStyle="md:text-lg text-sm"
                desStyle="md:text-base"
                title="No prescription orders"
                description="You don’t have any prescription orders yet. Upload a prescription above to place an order for medication fulfillment."
                buttons={[
                  { name: "Refresh", event: () => fetchData() },
                  {
                    name: "Upload prescription",
                    path: "/main/prescriptions/ordering",
                    type: "primary",
                  },
                ]}
              />
            </div>
          ) : showList ? (
            <>
              <ul
                className="space-y-3"
                aria-label="Prescription order list"
              >
                {data.map((order) => (
                  <li key={order.id}>
                    <PrescriptionOrderCard order={order} onViewDetails={handleViewDetails} />
                  </li>
                ))}
              </ul>
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={currentPage}
                    onChange={onChangePage}
                    // The API only returns a page count, not an item count,
                    // and antd's `total` prop means item count (it derives
                    // page buttons as ceil(total/pageSize)). Reconstructing
                    // total as totalPages*PAGE_SIZE reproduces the correct
                    // page count exactly, since the API computed totalPages
                    // the same way (ceil(realCount/PAGE_SIZE)).
                    total={totalPages * PAGE_SIZE}
                    pageSize={PAGE_SIZE}
                    showSizeChanger={false}
                    className="prescription-pagination"
                    aria-label="Prescription orders pagination"
                  />
                </div>
              )}
            </>
          ) : null}
        </section>
      </main>

      {/* Order detail drawer */}
      <Drawer
        title={
          detailOrder ? (
            <span className="font-semibold text-neutral-900">
              Order #{detailData?.orderId ?? detailOrder.orderId}
            </span>
          ) : (
            "Order details"
          )
        }
        placement="right"
        width={400}
        onClose={closeDetailDrawer}
        open={detailOrder !== null}
        destroyOnClose
        className="prescription-detail-drawer"
        styles={{ body: { paddingBottom: 24 } }}
      >
        {detailOrder && (
          <div className="flex flex-col h-full">
            {detailLoading ? (
              <Loader num={1} />
            ) : (
              <div className="overflow-y-auto pb-4">
                {detailData && (
                  <>
                    {/* Status & date */}
                    <section className="mb-5">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-md text-sm font-medium ${getStatusConfig(detailData.status ?? detailOrder.status).bgClass} ${getStatusConfig(detailData.status ?? detailOrder.status).colorClass}`}
                        >
                          {getStatusConfig(detailData.status ?? detailOrder.status).label}
                        </span>
                        {(detailData.paymentStatus != null && detailData.paymentStatus !== "") && (
                          <span className="text-xs text-neutral-500 capitalize">
                            Payment: {String(detailData.paymentStatus)}
                          </span>
                        )}
                      </div>
                      <DetailRow
                        label="Order date"
                        value={
                          detailData.createdAt
                            ? moment(detailData.createdAt).format("MMMM D, YYYY [at] h:mm A")
                            : detailData.timestamp
                              ? moment(detailData.timestamp).format("MMMM D, YYYY [at] h:mm A")
                              : detailOrder.schedule_date
                                ? moment(detailOrder.schedule_date).format("MMMM D, YYYY")
                                : "—"
                        }
                      />
                      {detailData.updatedAt && (
                        <DetailRow
                          label="Last updated"
                          value={moment(detailData.updatedAt).format("MMM D, YYYY h:mm A")}
                        />
                      )}
                    </section>

                    {/* Contact & delivery */}
                    <DetailSection title="Contact & delivery">
                      <DetailRow label="Name" value={detailData.name ?? detailData.user?.fullname} />
                      <DetailRow label="Email" value={detailData.email ?? detailData.user?.email} />
                      <DetailRow label="Phone" value={detailData.contact ?? detailData.user?.phoneNumber} />
                      <DetailRow
                        label="Address"
                        value={detailData.address ?? detailData.newAddress?.town}
                      />
                      <DetailRow label="Landmark" value={detailData.landmark ?? detailData.newAddress?.landmark} />
                      <DetailRow
                        label="Delivery"
                        value={detailData.deliveryMode?.title ?? detailData.delivery ?? null}
                      />
                    </DetailSection>

                    {/* Insurance */}
                    {(detailData.insurance ?? detailData.newInsurance?.provider ?? detailData.policyNumber ?? detailData.newInsurance?.policyNumber) && (
                      <DetailSection title="Insurance">
                        <DetailRow
                          label="Provider"
                          value={detailData.insurance ?? detailData.newInsurance?.provider}
                        />
                        <DetailRow
                          label="Policy number"
                          value={(() => {
                            const policyNumber =
                              detailData.policyNumber ?? detailData.newInsurance?.policyNumber;
                            return typeof policyNumber === "string"
                              ? maskPolicyNumber(policyNumber)
                              : policyNumber;
                          })()}
                        />
                      </DetailSection>
                    )}

                    {/* Payment */}
                    {(detailData.paymentStatus != null || detailData.totalAmount != null || detailData.deliveryCost != null || detailData.tax != null || detailData.payableAmount != null) && (
                      <DetailSection title="Payment">
                        <DetailRow label="Payment status" value={detailData.paymentStatus ? String(detailData.paymentStatus).replace(/_/g, " ") : null} />
                        <DetailRow label="Subtotal / total" value={detailData.totalAmount != null ? formatGHC(detailData.totalAmount) : null} />
                        <DetailRow label="Delivery cost" value={detailData.deliveryCost != null ? formatGHC(detailData.deliveryCost) : null} />
                        <DetailRow label="Tax" value={detailData.tax != null ? formatGHC(detailData.tax) : null} />
                        <DetailRow label="Payable amount" value={detailData.payableAmount != null ? formatGHC(detailData.payableAmount) : null} />
                      </DetailSection>
                    )}

                    {/* Medications (orderdetails) */}
                    {detailData.orderdetails && detailData.orderdetails.length > 0 && (
                      <DetailSection title="Medications">
                        <ul className="divide-y divide-neutral-200" aria-label="Medications on prescription">
                          {detailData.orderdetails.map((item, index) => {
                            const lineTotal = (item.quantity ?? 0) * (item.unitPrice ?? 0);
                            return (
                              <li key={item.id ?? index} className="py-3 first:pt-0 last:pb-0">
                                <p className="text-sm font-medium text-neutral-900">
                                  {item.drugName ?? "—"}
                                </p>
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-neutral-600">
                                  <span>Qty: {item.quantity ?? "—"}</span>
                                  <span>{formatGHC(item.unitPrice)} each</span>
                                  <span className="font-medium text-neutral-800">
                                    {formatGHC(lineTotal)} total
                                  </span>
                                </div>
                                {(item.dosage ?? item.frequency ?? item.route ?? item.instructions ?? item.duration ?? item.notes) && (
                                  <div className="mt-1.5 text-xs text-neutral-500 space-y-0.5">
                                    {item.dosage && <p>Dosage: {item.dosage}</p>}
                                    {item.frequency && <p>Frequency: {item.frequency}</p>}
                                    {item.route && <p>Route: {item.route}</p>}
                                    {item.duration && <p>Duration: {item.duration}</p>}
                                    {item.instructions && <p>{item.instructions}</p>}
                                    {item.notes && <p>Note: {item.notes}</p>}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </DetailSection>
                    )}

                    {/* Order details */}
                    {(detailData.orderType != null || detailData.refill != null || detailData.notes != null) && (
                      <DetailSection title="Order details">
                        <DetailRow label="Order type" value={detailData.orderType} />
                        <DetailRow label="Refill" value={detailData.refill != null ? String(detailData.refill) : null} />
                        <DetailRow label="Notes" value={detailData.notes} />
                      </DetailSection>
                    )}

                    {/* Cancel reason */}
                    {detailData.cancelReason && (
                      <DetailSection title="Cancellation">
                        <DetailRow label="Reason" value={detailData.cancelReason} />
                      </DetailSection>
                    )}

                    {/* Prescription image */}
                    {(detailData.image ?? detailOrder.image) && (
                      <DetailSection title="Prescription image">
                        <div className="rounded-lg border border-neutral-200 overflow-hidden bg-white">
                          <img
                            src={detailData.image ?? detailOrder.image ?? ""}
                            alt="Prescription"
                            className="w-full h-auto max-h-72 object-contain"
                          />
                        </div>
                      </DetailSection>
                    )}
                  </>
                )}

                {/* Fallback when no detail loaded yet or API returns minimal data */}
                {!detailData && (
                  <>
                    <div className="mb-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-md text-sm font-medium ${getStatusConfig(detailOrder.status).bgClass} ${getStatusConfig(detailOrder.status).colorClass}`}>
                        {getStatusConfig(detailOrder.status).label}
                      </span>
                    </div>
                    <DetailRow
                      label="Scheduled date"
                      value={detailOrder.schedule_date ? moment(detailOrder.schedule_date).format("MMMM D, YYYY") : "—"}
                    />
                    {detailOrder.image && (
                      <div className="mt-4">
                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide block mb-2">Prescription image</span>
                        <img src={detailOrder.image} alt="Prescription" className="w-full h-auto max-h-64 object-contain rounded-lg border border-neutral-200" />
                      </div>
                    )}
                  </>
                )}

                <div className="mt-6 pt-4 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 mb-3">
                    Need to place another order? Upload a new prescription below.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      closeDetailDrawer();
                      navigate("/main/prescriptions/ordering");
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#06509E] text-[#06509E] text-sm font-medium hover:bg-[#06509E] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#06509E] focus:ring-offset-2 transition-colors"
                    aria-label="Go to upload prescription"
                  >
                    <FileTextOutlined aria-hidden />
                    Upload new prescription
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      <ChatButton />

      {isChatOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-[10000]"
          role="dialog"
          aria-modal="true"
          aria-label="Kobikuul chat"
        >
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
            <button
              type="button"
              onClick={() => setIsChatOpen(false)}
              className="absolute top-2 right-2 text-neutral-600 hover:text-neutral-800 p-1"
              aria-label="Close chat"
            >
              ✕
            </button>
            <ChatInterface />
          </div>
        </div>
      )}

      <div className="fixed bottom-10 -right-2 z-[9999] transform scale-150 flex items-center">
        <FloatButton.Group
          trigger="click"
          type="default"
          style={{ insetInlineEnd: 24 }}
          icon={
            <img
              src={whatsapp}
              alt=""
              className="w-full h-full object-cover"
            />
          }
        >
          <FloatButton
            icon={
              <img
                src={whatsapp}
                alt=""
                className="w-full h-full object-cover"
              />
            }
            onClick={() =>
              window.open(
                "https://wa.me/233555061412?text=Hello%20Medpharma%20Team%2C%20I%20need%20assistance",
                "_blank",
                "noopener,noreferrer"
              )
            }
            aria-label="Chat on WhatsApp"
          />
          <FloatButton
            icon={<img src={kobi} alt="" className="w-full h-full object-cover" />}
            onClick={() => setIsChatOpen(true)}
            aria-label="Open Kobikuul health chat"
          />
        </FloatButton.Group>
      </div>
    </div>
  );
}
